import React, { useEffect, useState, useContext } from 'react';
import { useAuth } from '@teko/auth-react';
import { useTenantConfig } from 'hooks';
import { FCM_TOKEN, Notification as Noti } from '@teko/notification';
import { useServices } from 'contexts';

import { logger } from '@teko/log';

export enum PageStatus {
  LOADING = 'loading',
  LOADED = 'loaded',
  EMPTY = 'empty',
  FAILED = 'failed',
}

interface NotificationProviderProps {
  children: React.ReactNode;
}

interface INotificationContext {
  status: PageStatus;
  notifications: Noti[];
  checkpoint?: number;
  loading: boolean;
  getNotifications: (checkpoint?: number) => Promise<void>;
  handleNotificationClicked: (notification: Noti) => Promise<void>;
  markReadAll: () => Promise<void>;
  markAllReadLoading?: boolean;
  unRead: number;
}

export const NotificationContext = React.createContext<
  INotificationContext | undefined
>(undefined);

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotification must be used in NotificationProvider');
  }

  return context;
};

export const NotificationProvider = ({
  children,
}: NotificationProviderProps) => {
  const { user, loading: userLoading } = useAuth();
  const { notificationService, discoveryService } = useServices();
  const {
    notificationAppId: app_id,
    getProcessEnv,
    terminal,
  } = useTenantConfig();

  const [status, setStatus] = useState<PageStatus>(PageStatus.LOADED);
  const [notifications, setNotifications] = useState<Array<Noti>>([]);
  const [checkpoint, setCheckpoint] = useState(undefined);
  const [loading, setLoading] = useState(true);
  const [markAllReadLoading, setLoadingMarkReadAll] = useState(false);
  const [unRead, setUnread] = useState(undefined);

  const getMoreNotifications = async (
    limit = 10,
    checkpoint: number = undefined,
  ) => {
    const token = localStorage.getItem(FCM_TOKEN);
    const params = {
      app_id,
      limit: limit,
      ignore_pending_send: 0,
      check_point: checkpoint,
      token: token ?? undefined,
    };
    setLoading(true);

    try {
      const res = await notificationService.getNotification(params);
      if (res) {
        let apiResultNotifications = res.notifications;
        if (!!apiResultNotifications.length) {
          // call API discovery to get image of order notification
          // array of order notification's order items
          const notificationOrderItemsArr = apiResultNotifications
            .filter((noti) => !!noti.order)
            .map((noti) => noti.order.orderItems);

          if (notificationOrderItemsArr.length) {
            const products = await discoveryService.getProductDetailBySkus({
              skus: notificationOrderItemsArr,
              terminalCode: terminal,
            });

            const imageMapping: { [k: string]: string } = products.reduce(
              (prev: { [k: string]: string }, curr) => ({
                ...prev,
                [curr.productInfo?.sku]: curr.productInfo?.imageUrl,
              }),
              {},
            );

            apiResultNotifications = apiResultNotifications.map((noti) => {
              return {
                ...noti,
                ...(!noti.order
                  ? {}
                  : {
                      order: {
                        ...noti.order,
                        imageUrl: imageMapping[noti.order.orderItems] || '',
                      },
                    }),
              };
            });
          }
        }
        return {
          notifications: apiResultNotifications,
          checkpoint: res.check_point,
          unRead: res.unread,
        };
      }
    } catch (err) {
      logger.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getNotifications = async (
    checkpoint: number = undefined,
    limit = 10,
    isNew = false,
  ) => {
    const {
      checkpoint: newCheckpoint,
      notifications: newNotifications = [],
      unRead: newUnread,
    } = (await getMoreNotifications(limit, checkpoint)) || {};

    if (!isNew) {
      setCheckpoint(() => newCheckpoint);
      setNotifications((notifications) => [
        ...notifications,
        ...newNotifications,
      ]);
    } else {
      setNotifications((notifications) => [
        ...newNotifications,
        ...notifications,
      ]);
    }
    setUnread(() => newUnread);
  };

  const installWebPushNotification = async () => {
    // implement sequential 2 steps
    try {
      // Step 1: register token and save firebase token in localStorage.getItem('fcmToken');
      if ('serviceWorker' in navigator && typeof Notification !== 'undefined') {
        const permission = await Notification.requestPermission();

        if (permission === 'granted') {
          const firebaseConfig = {
            apiKey: getProcessEnv('FIREBASE_CLOUD_MESSAGING_API_KEY'),
            authDomain: getProcessEnv('FIREBASE_CLOUD_MESSAGING_AUTH_DOMAIN'),
            databaseURL: getProcessEnv('FIREBASE_CLOUD_MESSAGING_DATABASE_URL'),
            projectId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_PROJECT_ID'),
            storageBucket: getProcessEnv(
              'FIREBASE_CLOUD_MESSAGING_STORAGE_BUCKET',
            ),
            messagingSenderId: getProcessEnv(
              'FIREBASE_CLOUD_MESSAGING_MESSAGING_SENDER_ID',
            ),
            appId: getProcessEnv('FIREBASE_CLOUD_MESSAGING_APP_ID'),
            measurementId: getProcessEnv(
              'FIREBASE_CLOUD_MESSAGING_MEASUREMENT_ID',
            ),
          };
          const { registerFcmToken } = await import('helpers/notification');
          // register firebase messaging token
          registerFcmToken(
            firebaseConfig,
            getProcessEnv('FIREBASE_VAPID_KEY'),
            user,
            app_id,
            notificationService,
            () => getNotifications(undefined, 1, true),
          );
        }
      }
    } finally {
      // Step 2: use firebase token to get notifications
      getNotifications(checkpoint);
    }
  };

  useEffect(() => {
    if (!userLoading) {
      installWebPushNotification();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.userId, userLoading]);

  useEffect(() => {
    if (!loading) {
      if (notifications.length === 0) {
        setStatus(PageStatus.EMPTY);
      } else {
        setStatus(PageStatus.LOADED);
      }
    } else {
      setStatus(PageStatus.LOADING);
    }
  }, [loading, notifications]);

  const handleNotificationClicked = async (notification: Noti) => {
    if (!notification.is_read) {
      const token = localStorage.getItem(FCM_TOKEN);
      try {
        const { status } = await notificationService.markNotificationAsRead(
          notification.id,
          token,
        );
        if (status === 'success') {
          setNotifications((prev) =>
            prev.map((noti: Noti) => {
              if (noti.id === notification.id) {
                return {
                  ...noti,
                  is_read: true,
                };
              }
              return noti;
            }),
          );

          if (!notification.is_read) setUnread((count: number) => count - 1);
        }
      } catch (e) {
        logger.error(e);
      }
    }
  };

  const markReadAll = async () => {
    try {
      setLoadingMarkReadAll(true);
      const token = localStorage.getItem(FCM_TOKEN);
      const params = {
        app_id,
        token: token ?? undefined,
      };

      await notificationService.markReadAll(params);

      setNotifications((notifications) =>
        notifications.map((noti) => ({ ...noti, is_read: true })),
      );
      setUnread(0);
    } catch (e) {
      logger.error(e);
    } finally {
      setLoadingMarkReadAll(false);
    }
  };

  return (
    <NotificationContext.Provider
      value={{
        status,
        notifications,
        checkpoint,
        loading,
        getNotifications,
        handleNotificationClicked,
        markReadAll,
        markAllReadLoading,
        unRead,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
